/* The mobile sticky Add to cart bar (blocks/st-sticky-buy.liquid says what it is for).

   It shows whenever the real Add to cart button is off the screen, below it as well as above, and
   hides while the real one is in view. Below matters: on a phone the real button sits about a screen
   down, so a visitor scrolling towards it had no price and no button on the way (parity review
   2026-10-09). It mirrors the pack label and
   the price from the pack picker and pack total (which change on every pack or variant change,
   including Horizon's morph of the section), and its button presses the real one, so the cart logic
   lives in one place. Phones only; the media query is the same 750px line as the stylesheet.
   The homepage buy box (sections/st-buy.liquid) uses it too, with two extra rules: its section
   marks itself [data-st-sbuy-arm], and the bar waits until that is [data-st-used] (the visitor has
   chosen a platform or a count), so it does not follow everyone down the homepage; and it hides
   while the footer is on screen. Its label comes from [data-st-sbuy-label]. It also hides while a
   [data-st-sbuy-peer] (the buy box's total) is wholly on screen: on a phone the counter sits about
   260px above the total, so picking a count, the bar had covered the very total it repeats
   (Paulius, 2026-10-10). On the product page the pack line is the counter's count and noun
   ("50 cards"); it had been blank since the counter lost its <output>.
   A platform switch on the product page (assets/st-platform-swap.js) replaces the bar with its
   section, so a bar that leaves the page lets go of the window and of the page's bottom padding:
   an old bar still listening had taken the padding away from the new one (2026-10-10). */
(() => {
  const PHONE = window.matchMedia('(max-width: 749px)');

  class StStickyBuy extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.section = this.closest('.shopify-section') || document;
      this.button = this.querySelector('[data-st-sbuy-add]');
      this.button.addEventListener(
        'click',
        () => {
          const real = this.real();
          if (!real || real.disabled) return;
          real.click();
          document.dispatchEvent(new CustomEvent('st:sticky-buy', { detail: { pack: this.packText } }));
        },
        { signal }
      );
      this.sync = this.sync.bind(this);
      this.watch();
      // Changes this bar makes to itself are ignored, or writing its own text would retrigger it.
      this.observer = new MutationObserver((records) => {
        if (records.every((r) => this.contains(r.target))) return;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(this.sync);
      });
      this.observer.observe(this.section, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-cents', 'data-st-used'],
      });
      PHONE.addEventListener('change', () => this.place(), { signal });
      this.sync();
    }

    disconnectedCallback() {
      this.abort.abort();
      this.observer.disconnect();
      cancelAnimationFrame(this.frame);
      this.listening = false;
      // The bottom padding was this bar's; a bar that takes its place sets its own.
      if (!this.hidden) document.body.classList.remove('st-sbuy-on');
    }

    real() {
      return [...this.section.querySelectorAll('button[name="add"]')].find((b) => !this.contains(b) && b.offsetParent);
    }

    /** Track the real button. A scroll check rather than an IntersectionObserver: a fast fling or an
        anchor jump can carry the button from below the screen to above it in one frame, and an
        observer only reports a change of visibility, so it would never fire (measured: a 1,500px
        jump left the bar hidden). One read per frame, passive listeners. */
    watch() {
      this.watched = this.real();
      if (this.listening) return;
      this.listening = true;
      const check = () => {
        this.ticking = false;
        const real = this.watched;
        const r = real && real.isConnected ? real.getBoundingClientRect() : null;
        this.past = Boolean(r && (r.bottom < 0 || r.top > window.innerHeight));
        const peer = this.section.querySelector('[data-st-sbuy-peer]');
        const p = peer && peer.offsetParent ? peer.getBoundingClientRect() : null;
        this.peerOn = Boolean(p && p.top >= 0 && p.bottom <= window.innerHeight);
        const footer = document.querySelector('.st-ftr');
        this.atFooter = Boolean(footer && footer.getBoundingClientRect().top < window.innerHeight);
        this.place();
      };
      this.check = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(check);
      };
      window.addEventListener('scroll', this.check, { passive: true, signal: this.abort.signal });
      window.addEventListener('resize', this.check, { passive: true, signal: this.abort.signal });
      this.check();
    }

    place() {
      const armed = !this.section.querySelector?.('[data-st-sbuy-arm]:not([data-st-used])');
      const on = Boolean(this.past && !this.peerOn && !this.atFooter && armed && PHONE.matches && this.watched);
      if (on === !this.hidden) return;
      this.hidden = !on;
      document.body.classList.toggle('st-sbuy-on', on);
      if (on) document.body.style.setProperty('--st-sbuy-h', `${this.offsetHeight}px`);
    }

    sync() {
      this.watch();
      const label = this.section.querySelector('[data-st-sbuy-label]');
      const picker = this.section.querySelector('st-pack-picker');
      const noun = picker?.querySelector('.st-pk__noun');
      const count = picker?.count ?? picker?.querySelector('.st-pk__n')?.value;
      const price = this.section.querySelector('.st-pk-total__now');
      const real = this.watched;
      const set = (el, text) => {
        if (el.textContent !== text) el.textContent = text;
      };
      if (label) this.packText = label.textContent.trim();
      else this.packText = count && noun ? `${count} ${noun.textContent.trim()}` : '';
      set(this.querySelector('[data-st-sbuy-pack]'), this.packText);
      set(this.querySelector('[data-st-sbuy-price]'), price ? price.textContent.trim() : '');
      if (real) {
        if (this.button.disabled !== real.disabled) this.button.disabled = real.disabled;
        const label = real.textContent.trim().replace(/\s+/g, ' ');
        if (label) set(this.button, label);
      }
      // A change of count moves the total and arms the bar without a scroll, so check again.
      this.check?.();
    }
  }

  if (!customElements.get('st-sticky-buy')) customElements.define('st-sticky-buy', StStickyBuy);
})();
